import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Colors - Lighter palette
const colors = {
  primary: [0, 179, 171],       // Lighter teal #00b3ab
  primaryLight: [204, 251, 241], // Very light teal #ccfbf1
  secondary: [71, 85, 105],     // Slate #475569
  dark: [30, 41, 59],           // #1e293b
  muted: [100, 116, 139],       // #64748b
  border: [203, 213, 225],      // #cbd5e1
  lightBg: [248, 250, 252],     // #f8fafc
  white: [255, 255, 255],
};

// Helper functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatShortDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatMonthYear = (dateString) => {
  return dateString
    ? new Date(dateString).toLocaleDateString('en-MY', { year: 'numeric', month: 'short' })
    : '—';
};

const formatDateTime = () => {
  return new Date().toLocaleString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

// Get period date range with short month
const getPeriodRange = (month, year) => {
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0);
  
  const formatPeriodDate = (date) => {
    return date.toLocaleDateString('en-MY', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };
  
  return `${formatPeriodDate(startDate)} - ${formatPeriodDate(endDate)}`;
};

// Commission Report PDF Generator
export const generateCommissionPDF = async (data, user, period) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Parse period to get month and year
  const periodParts = period.split(' ');
  const monthName = periodParts[0];
  const year = parseInt(periodParts[1]);
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  const month = months.indexOf(monthName) + 1;
  const periodRange = getPeriodRange(month, year);
  
  let currentY = 15;
  
  // === HEADER SECTION ===
  // Logo (top left)
  try {
    const logoImg = new Image();
    logoImg.crossOrigin = 'Anonymous';
    
    await new Promise((resolve, reject) => {
      logoImg.onload = resolve;
      logoImg.onerror = reject;
      logoImg.src = '/images/YDG_LOGO.png';
    });
    
    const logoWidth = 35;
    const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
    doc.addImage(logoImg, 'PNG', 14, currentY, logoWidth, logoHeight);
  } catch (error) {
    doc.setFontSize(20);
    doc.setTextColor(...colors.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('YDG', 14, currentY + 12);
  }
  
  // Title (center) - positioned lower than logo
  doc.setFontSize(18);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'bold');
  doc.text('COMMISSION STATEMENT', pageWidth / 2, currentY + 22, { align: 'center' });
  
  currentY += 40;
  
  // Divider line
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.5);
  doc.line(14, currentY, pageWidth - 14, currentY);
  
  currentY += 10;
  
  // === USER INFO SECTION ===
  const leftX = 14;
  const rightX = pageWidth - 14;
  const labelWidth = 28;
  
  // Left column - User details
  doc.setFontSize(10);
  
  // NAME
  doc.setTextColor(...colors.muted);
  doc.setFont('helvetica', 'normal');
  doc.text('NAME', leftX, currentY);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'bold');
  doc.text(`: ${user.name || 'N/A'}`, leftX + labelWidth, currentY);
  
  // PERIOD (right side)
  doc.setTextColor(...colors.muted);
  doc.setFont('helvetica', 'normal');
  doc.text('PERIOD', rightX - 85, currentY);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'normal');
  doc.text(`: ${periodRange}`, rightX - 85 + 18, currentY);
  
  currentY += 6;
  
  // CODE (under name)
  doc.setTextColor(...colors.muted);
  doc.setFont('helvetica', 'normal');
  doc.text('CODE', leftX, currentY);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'normal');
  doc.text(`: ${user.accountId || user.code || 'N/A'}`, leftX + labelWidth, currentY);
  
  // STATEMENT DATE (right side)
  doc.setTextColor(...colors.muted);
  doc.text('STATEMENT DATE', rightX - 85, currentY);
  doc.setTextColor(...colors.dark);
  doc.text(`: ${formatShortDate(new Date())}`, rightX - 85 + 38, currentY);
  
  currentY += 6;
  
  // ROLE
  doc.setTextColor(...colors.muted);
  doc.setFont('helvetica', 'normal');
  doc.text('ROLE', leftX, currentY);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'normal');
  doc.text(`: ${user.role || 'N/A'}`, leftX + labelWidth, currentY);
  
  currentY += 6;
  
  // GROUP
  doc.setTextColor(...colors.muted);
  doc.setFont('helvetica', 'normal');
  doc.text('GROUP', leftX, currentY);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'normal');
  doc.text(`: ${user.group || 'N/A'}`, leftX + labelWidth, currentY);
  
  currentY += 6;
  
  // REPORT TO
  doc.setTextColor(...colors.muted);
  doc.setFont('helvetica', 'normal');
  doc.text('REPORT TO', leftX, currentY);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'normal');
  doc.text(`: ${user.reportsTo || 'N/A'}`, leftX + labelWidth, currentY);
  
  currentY += 12;
  
  // Divider line
  doc.setDrawColor(...colors.border);
  doc.line(14, currentY, pageWidth - 14, currentY);
  
  currentY += 10;
  
  // === COMMISSION SUMMARY ===
  doc.setFontSize(11);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'bold');
  doc.text('COMMISSION SUMMARY', leftX, currentY);
  
  currentY += 8;
  
  // Summary table - filter rows based on user role
  // Agent: only Personal
  // Team Leader: Personal + Team Override
  // Group Leader / others: all three
  const userRole = (user.role || '').toLowerCase();
  const isAgent = userRole.includes('agent');
  const isTeamLeader = userRole.includes('team') && userRole.includes('leader');
  
  const summaryData = [];
  summaryData.push(['Personal Commission', formatCurrency(data.summary?.totalPersonal || 0)]);
  
  if (!isAgent) {
    summaryData.push(['Team Override Commission', formatCurrency(data.summary?.totalTLOverride || 0)]);
  }
  
  if (!isAgent && !isTeamLeader) {
    summaryData.push(['Group Override Commission', formatCurrency(data.summary?.totalGLOverride || 0)]);
  }
  
  // Add retroactive commission row if exists
  const hasRetroactive = data.retroactiveCommissions && data.retroactiveCommissions.length > 0;
  if (hasRetroactive) {
    const totalRetroactive = data.retroactiveCommissions.reduce(
      (sum, r) => sum + (parseFloat(r.commissionAmount) || 0), 0
    );
    summaryData.push(['Retroactive Commission (Prior Period)', formatCurrency(totalRetroactive)]);
  }
  
  // Add total row - include retroactive if exists
  const grandTotal = (data.summary?.totalEligible || 0) + 
    (hasRetroactive ? data.retroactiveCommissions.reduce((sum, r) => sum + (parseFloat(r.commissionAmount) || 0), 0) : 0);
  summaryData.push(['TOTAL ELIGIBLE COMMISSION', formatCurrency(grandTotal)]);
  
  const totalRowIndex = summaryData.length - 1;
  
  let summaryFinalY = currentY;
  autoTable(doc, {
    startY: currentY,
    head: [['Commission Type', 'Eligible Amount (MYR)']],
    body: summaryData,
    theme: 'grid',
    headStyles: {
      fillColor: colors.primary,
      textColor: colors.white,
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 4,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 4,
      textColor: colors.dark,
    },
    columnStyles: {
      0: { cellWidth: 130, halign: 'left' },
      1: { cellWidth: 52, halign: 'right' },
    },
    margin: { left: 14, right: 14 },
    tableWidth: 182,
    tableLineColor: colors.border,
    tableLineWidth: 0.5,
    didParseCell: (cellData) => {
      // Style the last row (total row) differently
      if (cellData.section === 'body' && cellData.row.index === totalRowIndex) {
        cellData.cell.styles.fillColor = colors.secondary;
        cellData.cell.styles.textColor = colors.white;
        cellData.cell.styles.fontStyle = 'bold';
        cellData.cell.styles.fontSize = 10;
      }
    },
    didDrawPage: (hookData) => {
      summaryFinalY = hookData.cursor.y;
    },
  });
  
  currentY = summaryFinalY + 15;
  
  // === COMMISSION DETAILS ===
  doc.setFontSize(11);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'bold');
  doc.text('COMMISSION DETAILS', leftX, currentY);
  
  currentY += 8;
  
  // Build details data - show eligible amount in Commission column
  // Filter based on user role
  // Collect all records first, then sort by date ascending
  const allCommissionRecords = [];
  
  if (data.products && data.products.length > 0) {
    data.products.forEach(product => {
      // Personal commissions - always show for all roles
      if (product.personal?.records?.length > 0) {
        const personalTotal = product.personal?.total || 0;
        const personalEligible = product.personal?.eligible || 0;
        const personalRatio = personalTotal > 0 ? personalEligible / personalTotal : 0;
        
        product.personal.records.forEach(rec => {
          const eligibleAmount = parseFloat(rec.amount) * personalRatio;
          allCommissionRecords.push({
            date: rec.date,
            clientName: rec.clientName || 'N/A',
            product: product.productName,
            type: 'Personal',
            saleAmount: rec.saleAmount,
            rate: rec.rate,
            amount: eligibleAmount
          });
        });
      }
      
      // Team Override commissions - show for TL and GL only (not Agent)
      if (!isAgent && product.tlOverride?.records?.length > 0) {
        const tlTotal = product.tlOverride?.total || 0;
        const tlEligible = product.tlOverride?.eligible || 0;
        const tlRatio = tlTotal > 0 ? tlEligible / tlTotal : 0;
        
        product.tlOverride.records.forEach(rec => {
          const eligibleAmount = parseFloat(rec.amount) * tlRatio;
          allCommissionRecords.push({
            date: rec.date,
            clientName: rec.clientName || 'N/A',
            product: product.productName,
            type: 'Team Override',
            saleAmount: rec.saleAmount,
            rate: rec.rate,
            amount: eligibleAmount
          });
        });
      }
      
      // Group Override commissions - show for GL only (not Agent, not TL)
      if (!isAgent && !isTeamLeader && product.glOverride?.records?.length > 0) {
        const glTotal = product.glOverride?.total || 0;
        const glEligible = product.glOverride?.eligible || 0;
        const glRatio = glTotal > 0 ? glEligible / glTotal : 0;
        
        product.glOverride.records.forEach(rec => {
          const eligibleAmount = parseFloat(rec.amount) * glRatio;
          allCommissionRecords.push({
            date: rec.date,
            clientName: rec.clientName || 'N/A',
            product: product.productName,
            type: 'Group Override',
            saleAmount: rec.saleAmount,
            rate: rec.rate,
            amount: eligibleAmount
          });
        });
      }
    });
  }
  
  // Sort all records by date ascending
  allCommissionRecords.sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Build the table data
  const detailsData = allCommissionRecords.map(rec => [
    formatDate(rec.date),
    rec.clientName,
    rec.product,
    rec.type,
    formatCurrency(rec.saleAmount),
    `${(parseFloat(rec.rate) * 100).toFixed(2)}%`,
    formatCurrency(rec.amount), // This is now the eligible amount
  ]);
  
  if (detailsData.length === 0) {
    detailsData.push([
      { content: 'No commission records for this period', colSpan: 7, styles: { halign: 'center', fontStyle: 'italic', textColor: colors.muted } }
    ]);
  }
  
  let detailsFinalY = currentY;
  autoTable(doc, {
    startY: currentY,
    head: [['Date', 'Client', 'Product', 'Tier', 'Sales Amt.', 'Rate', 'Comm.']],
    body: detailsData,
    theme: 'grid',
    headStyles: {
      fillColor: colors.primary,
      textColor: colors.white,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: 4,
      halign: 'center',
      valign: 'middle',
    },
    bodyStyles: {
      fontSize: 7,
      cellPadding: 3,
      textColor: colors.dark,
    },
    alternateRowStyles: {
      fillColor: colors.lightBg,
    },
    columnStyles: {
      0: { cellWidth: 20 },
      1: { cellWidth: 34 },
      2: { cellWidth: 40 },
      3: { cellWidth: 24 },
      4: { cellWidth: 28, halign: 'right' },
      5: { cellWidth: 14, halign: 'center' },
      6: { cellWidth: 22, halign: 'right' },
    },
    tableWidth: 182,
    margin: { left: 14, right: 14 },
    tableLineColor: colors.border,
    tableLineWidth: 0.5,
    didDrawPage: (hookData) => {
      detailsFinalY = hookData.cursor.y;
    },
  });
  
  currentY = detailsFinalY + 15;
  
  // === RETROACTIVE COMMISSION SECTION ===
  // Only show if there are retroactive commissions
  if (data.retroactiveCommissions && data.retroactiveCommissions.length > 0) {
    // Check if we need a new page
    if (currentY > pageHeight - 60) {
      doc.addPage();
      currentY = 20;
    }
    
    doc.setFontSize(11);
    doc.setTextColor(...colors.dark);
    doc.setFont('helvetica', 'bold');
    doc.text('RETROACTIVE COMMISSION (From Prior Period Sales)', leftX, currentY);
    
    currentY += 4;
    
    // Add explanation text
    doc.setFontSize(8);
    doc.setTextColor(...colors.muted);
    doc.setFont('helvetica', 'italic');
    doc.text('These commissions became eligible after the threshold was met within the campaign period.', leftX, currentY + 4);
    
    currentY += 12;
    
    // Sort retroactive data by sale date ascending
    const sortedRetroactive = [...data.retroactiveCommissions].sort(
      (a, b) => new Date(a.saleDate) - new Date(b.saleDate)
    );
    
    // Build retroactive data with client name
    const retroData = sortedRetroactive.map(retro => [
      formatDate(retro.saleDate),
      formatDate(retro.eligibleDate),
      retro.clientName || 'N/A',
      retro.productName || 'N/A',
      retro.commissionType === 'TL Override' ? 'Team O/R' : retro.commissionType === 'GL Override' ? 'Group O/R' : retro.commissionType,
      formatCurrency(retro.saleAmount),
      `${(parseFloat(retro.commissionRate) * 100).toFixed(2)}%`,
      formatCurrency(retro.commissionAmount),
    ]);
    
    // Calculate total retroactive amount
    const totalRetroactive = data.retroactiveCommissions.reduce(
      (sum, r) => sum + (parseFloat(r.commissionAmount) || 0), 0
    );
    
    // Add total row
    retroData.push([
      { content: 'TOTAL RETROACTIVE COMMISSION', colSpan: 7, styles: { fontStyle: 'bold', halign: 'left', fillColor: colors.secondary, textColor: colors.white, lineWidth: 0 } },
      { content: formatCurrency(totalRetroactive), colSpan: 1, styles: { fontStyle: 'bold', fillColor: colors.secondary, textColor: colors.white, lineWidth: 0 } },
      { content: '', styles: { fillColor: colors.secondary } },
    ]);
    
    autoTable(doc, {
      startY: currentY,
      head: [['Sales Date', 'Eligible Date', 'Client', 'Product', 'Tier', 'Sales Amt.', 'Rate', 'Comm.']],
      body: retroData,
      theme: 'grid',
      headStyles: {
        fillColor: [230, 126, 34], // Orange for retroactive
        textColor: colors.white,
        fontStyle: 'bold',
        fontSize: 8,
        cellPadding: 3,
        halign: 'center',
        valign: 'middle',
      },
      bodyStyles: {
        fontSize: 7,
        cellPadding: 2,
        textColor: colors.dark,
      },
      alternateRowStyles: {
        fillColor: [255, 248, 240], // Light orange background
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 20 },
        2: { cellWidth: 30 },
        3: { cellWidth: 34 },
        4: { cellWidth: 18, halign: 'center' },
        5: { cellWidth: 24, halign: 'center' },
        6: { cellWidth: 14, halign: 'center' },
        7: { cellWidth: 22 },
      },
      tableWidth: 182,
      margin: { left: 14, right: 14 },
      tableLineColor: colors.border,
      tableLineWidth: 0.5,
    });
  }
  
  // Add footer to all pages with date/time
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...colors.muted);
    doc.text(`Page ${i} of ${totalPages}`, 14, pageHeight - 10, { align: 'left' });
    doc.text(`This is a computer-generated document. Generated on ${formatDateTime()}`, pageWidth - 14, pageHeight - 10, { align: 'right' });
  }
  
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  return pdfUrl;
};

// Sales Report PDF — grouped-by-group layout matches commission summary report (full-width tables)
export const generateSalesReportPDF = async (data, period, title = 'Sales Summary Report', options = {}) => {
  const { groupByGroup = true } = options;
  const margin = 14;
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - 2 * margin;

  let currentY = 15;

  try {
    const logoImg = new Image();
    logoImg.crossOrigin = 'Anonymous';
    await new Promise((resolve, reject) => {
      logoImg.onload = resolve;
      logoImg.onerror = reject;
      logoImg.src = '/images/YDG_LOGO.png';
    });
    const logoWidth = 35;
    const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
    doc.addImage(logoImg, 'PNG', margin, currentY, logoWidth, logoHeight);
  } catch (error) {
    doc.setFontSize(20);
    doc.setTextColor(...colors.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('YDG', margin, currentY + 12);
  }

  doc.setFontSize(16);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'bold');
  doc.text(title.toUpperCase(), pageWidth / 2, currentY + 22, { align: 'center' });

  currentY += 40;

  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY, pageWidth - margin, currentY);

  currentY += 10;

  doc.setFontSize(10);
  doc.setTextColor(...colors.muted);
  doc.text('PERIOD', margin, currentY);
  doc.setTextColor(...colors.dark);
  doc.text(`: ${period}`, 40, currentY);

  doc.setTextColor(...colors.muted);
  doc.text('GENERATED', pageWidth - 80, currentY);
  doc.setTextColor(...colors.dark);
  doc.text(`: ${formatDateTime()}`, pageWidth - 55, currentY);

  currentY += 15;

  const saleAmount = (sale) => parseFloat(sale.amount) || 0;
  const totalAmount = data.reduce((sum, sale) => sum + saleAmount(sale), 0);

  const addFooters = () => {
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(...colors.muted);
      doc.text(`Page ${i} of ${totalPages}`, margin, pageHeight - 10, { align: 'left' });
      doc.text(`This is a computer-generated document. Generated on ${formatDateTime()}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    }
  };

  if (!groupByGroup) {
    const tableData = data.map((sale) => [
      formatDate(sale.date),
      sale.user || 'N/A',
      sale.product || 'N/A',
      formatCurrency(sale.amount),
      sale.group || 'Unassigned',
    ]);
    autoTable(doc, {
      startY: currentY,
      head: [['Date', 'Sales Person', 'Product', 'Amount (MYR)', 'Group']],
      body: tableData,
      foot: [['', '', 'TOTAL', formatCurrency(totalAmount), '']],
      theme: 'grid',
      tableWidth: contentWidth,
      headStyles: {
        fillColor: colors.primary,
        textColor: colors.white,
        fontStyle: 'bold',
        fontSize: 9,
        cellPadding: 4,
        lineColor: colors.border,
        lineWidth: 0.1,
      },
      footStyles: {
        fillColor: colors.secondary,
        textColor: colors.white,
        fontStyle: 'bold',
        fontSize: 9,
        cellPadding: 4,
      },
      bodyStyles: { fontSize: 9, cellPadding: 3, textColor: colors.dark, lineColor: colors.border, lineWidth: 0.1 },
      alternateRowStyles: { fillColor: colors.lightBg },
      columnStyles: {
        0: { cellWidth: contentWidth * (22 / 180) },
        1: { cellWidth: contentWidth * (38 / 180) },
        2: { cellWidth: contentWidth * (62 / 180) },
        3: { cellWidth: contentWidth * (33 / 180), halign: 'right' },
        4: { cellWidth: contentWidth * (25 / 180) },
      },
      margin: { left: margin, right: margin },
    });
    addFooters();
    const pdfBlob = doc.output('blob');
    return URL.createObjectURL(pdfBlob);
  }

  // Grouped summary (default): overall summary + details by group
  const grouped = {};
  data.forEach((sale) => {
    const grpName = sale.group || 'Unassigned';
    if (!grouped[grpName]) grouped[grpName] = [];
    grouped[grpName].push(sale);
  });

  const numTransactions = data.length;
  const numGroups = Object.keys(grouped).length;

  const summaryRows = [
    ['Number of sales transactions', String(numTransactions)],
    ['Number of groups', String(numGroups)],
    ['GRAND TOTAL SALES (MYR)', formatCurrency(totalAmount)],
  ];
  const summaryTotalRowIdx = summaryRows.length - 1;

  let summaryEndY = currentY;
  autoTable(doc, {
    startY: currentY,
    head: [['Description', 'Amount / Count']],
    body: summaryRows,
    theme: 'grid',
    tableWidth: contentWidth,
    headStyles: {
      fillColor: colors.primary,
      textColor: colors.white,
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 4,
      lineColor: colors.border,
      lineWidth: 0.1,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 4,
      textColor: colors.dark,
      lineColor: colors.border,
      lineWidth: 0.1,
    },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.62, halign: 'left' },
      1: { cellWidth: contentWidth * 0.38, halign: 'right' },
    },
    margin: { left: margin, right: margin },
    didParseCell: (hookData) => {
      if (hookData.section === 'body' && hookData.row.index === summaryTotalRowIdx) {
        hookData.cell.styles.fillColor = colors.secondary;
        hookData.cell.styles.textColor = colors.white;
        hookData.cell.styles.fontStyle = 'bold';
        hookData.cell.styles.fontSize = 10;
      }
    },
    didDrawPage: (hookData) => {
      summaryEndY = hookData.cursor.y;
    },
  });

  currentY = summaryEndY + 15;

  doc.setFontSize(11);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'bold');
  doc.text('SALES DETAILS BY GROUP', margin, currentY);
  currentY += 8;

  const colRatios = [24, 40, 68, 48];
  const ratioSum = colRatios.reduce((a, b) => a + b, 0);
  const colWidths = colRatios.map((r) => (r / ratioSum) * contentWidth);

  const detailsBody = [];
  Object.keys(grouped)
    .sort()
    .forEach((grpName) => {
      const rows = grouped[grpName];
      detailsBody.push([
        {
          content: grpName,
          colSpan: 4,
          styles: { fillColor: [230, 240, 240], fontStyle: 'bold', fontSize: 9, halign: 'left' },
        },
      ]);
      rows
        .slice()
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .forEach((sale) => {
          detailsBody.push([
            formatDate(sale.date),
            sale.user || 'N/A',
            sale.product || 'N/A',
            formatCurrency(sale.amount),
          ]);
        });
      const sub = rows.reduce((s, sale) => s + saleAmount(sale), 0);
      detailsBody.push([
        { content: 'Subtotal', colSpan: 3, styles: { fontStyle: 'bold', halign: 'right' } },
        { content: formatCurrency(sub), styles: { fontStyle: 'bold', halign: 'right' } },
      ]);
    });

  detailsBody.push([
    {
      content: 'GRAND TOTAL',
      colSpan: 3,
      styles: { fontStyle: 'bold', halign: 'right', fillColor: colors.secondary, textColor: colors.white },
    },
    {
      content: formatCurrency(totalAmount),
      styles: { fontStyle: 'bold', halign: 'right', fillColor: colors.secondary, textColor: colors.white },
    },
  ]);

  autoTable(doc, {
    startY: currentY,
    head: [['Date', 'Sales Person', 'Product', 'Amount (MYR)']],
    body: detailsBody,
    theme: 'grid',
    tableWidth: contentWidth,
    headStyles: {
      fillColor: colors.primary,
      textColor: colors.white,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: 3,
      lineColor: colors.border,
      lineWidth: 0.1,
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 2,
      textColor: colors.dark,
      lineColor: colors.border,
      lineWidth: 0.1,
    },
    columnStyles: {
      0: { cellWidth: colWidths[0] },
      1: { cellWidth: colWidths[1] },
      2: { cellWidth: colWidths[2] },
      3: { cellWidth: colWidths[3], halign: 'right' },
    },
    margin: { left: margin, right: margin },
  });

  addFooters();
  const pdfBlob = doc.output('blob');
  return URL.createObjectURL(pdfBlob);
};

// Sales Comparison PDF (2 groups side-by-side in landscape)
export const generateSalesComparisonPDF = async (data, period, groupNameA, groupNameB) => {
  const doc = new jsPDF({ orientation: 'landscape' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const gap = 10;

  const contentWidth = pageWidth - 2 * margin;
  const columnWidth = (contentWidth - gap) / 2;

  const leftA = margin;
  const rightA = pageWidth - (leftA + columnWidth);
  const leftB = margin + columnWidth + gap;
  const rightB = pageWidth - (leftB + columnWidth);

  let currentY = 15;

  // Logo (top left)
  try {
    const logoImg = new Image();
    logoImg.crossOrigin = 'Anonymous';
    await new Promise((resolve, reject) => {
      logoImg.onload = resolve;
      logoImg.onerror = reject;
      logoImg.src = '/images/YDG_LOGO.png';
    });
    const logoWidth = 35;
    const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
    doc.addImage(logoImg, 'PNG', margin, currentY, logoWidth, logoHeight);
  } catch (error) {
    doc.setFontSize(20);
    doc.setTextColor(...colors.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('YDG', margin, currentY + 12);
  }

  // Title (center)
  doc.setFontSize(16);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'bold');
  doc.text('SALES COMPARISON REPORT', pageWidth / 2, currentY + 22, { align: 'center' });

  currentY += 40;

  // Divider
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY, pageWidth - margin, currentY);

  currentY += 10;

  // Period + generated
  doc.setFontSize(10);
  doc.setTextColor(...colors.muted);
  doc.text('PERIOD', margin, currentY);
  doc.setTextColor(...colors.dark);
  doc.text(`: ${period}`, 40, currentY);

  doc.setTextColor(...colors.muted);
  doc.text('GENERATED', pageWidth - 90, currentY);
  doc.setTextColor(...colors.dark);
  doc.text(`: ${formatShortDate(new Date())}`, pageWidth - 35, currentY);

  currentY += 15;

  const addFooters = () => {
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(...colors.muted);
      doc.text(`Page ${i} of ${totalPages}`, margin, pageHeight - 10, { align: 'left' });
      doc.text(
        `This is a computer-generated document. Generated on ${formatDateTime()}`,
        pageWidth - margin,
        pageHeight - 10,
        { align: 'right' }
      );
    }
  };

  const renderGroupColumn = (groupName, salesForGroup, startY, marginLeft, marginRight) => {
    let y = startY;

    doc.setFontSize(11);
    doc.setTextColor(...colors.dark);
    doc.setFont('helvetica', 'bold');
    doc.text(`GROUP: ${groupName.toUpperCase()}`, marginLeft, y);
    y += 6;

    const tableData = (salesForGroup || []).map((sale) => [
      formatDate(sale.date),
      sale.user || 'N/A',
      sale.product || 'N/A',
      formatCurrency(sale.amount),
    ]);

    const total = (salesForGroup || []).reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);

    autoTable(doc, {
      startY: y,
      head: [['Date', 'Sales Person', 'Product', 'Amount (MYR)']],
      body: tableData,
      // Render TOTAL as a single full-width row.
      foot: [[{ content: `TOTAL ${formatCurrency(total)}`, colSpan: 4, styles: { halign: 'right' } }]],
      theme: 'grid',
      tableWidth: columnWidth,
      headStyles: {
        fillColor: colors.primary,
        textColor: colors.white,
        fontStyle: 'bold',
        fontSize: 8,
        cellPadding: 2,
        lineColor: colors.border,
        lineWidth: 0.1,
      },
      footStyles: {
        fillColor: colors.secondary,
        textColor: colors.white,
        fontStyle: 'bold',
        fontSize: 8,
        cellPadding: 2,
      },
      bodyStyles: {
        fontSize: 8,
        textColor: colors.dark,
        cellPadding: 1.5,
        lineColor: colors.border,
        lineWidth: 0.1,
      },
      alternateRowStyles: { fillColor: colors.lightBg },
      columnStyles: {
        // Narrow Date + Sales Person; give more room for Amount to stay in one line.
        0: { cellWidth: columnWidth * 0.18 },
        1: { cellWidth: columnWidth * 0.22 },
        2: { cellWidth: columnWidth * 0.40 },
        3: { cellWidth: columnWidth * 0.20, halign: 'right' },
      },
      margin: { left: marginLeft, right: marginRight },
    });

    return doc.lastAutoTable.finalY;
  };

  const salesA = (data || []).filter((s) => (s.group || '') === groupNameA);
  const salesB = (data || []).filter((s) => (s.group || '') === groupNameB);

  const colStartY = currentY;
  // Render shorter first so we keep the other column starting on the first page
  // (autoTable paginates independently per call).
  if ((salesA || []).length <= (salesB || []).length) {
    renderGroupColumn(groupNameA, salesA, colStartY, leftA, rightA);
    renderGroupColumn(groupNameB, salesB, colStartY, leftB, rightB);
  } else {
    renderGroupColumn(groupNameB, salesB, colStartY, leftB, rightB);
    renderGroupColumn(groupNameA, salesA, colStartY, leftA, rightA);
  }

  addFooters();

  const pdfBlob = doc.output('blob');
  return URL.createObjectURL(pdfBlob);
};

// Commission Payout Summary Report
export const generatePayoutReportPDF = async (data, period) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  let currentY = 15;
  
  // Logo (top left)
  try {
    const logoImg = new Image();
    logoImg.crossOrigin = 'Anonymous';
    
    await new Promise((resolve, reject) => {
      logoImg.onload = resolve;
      logoImg.onerror = reject;
      logoImg.src = '/images/YDG_LOGO.png';
    });
    
    const logoWidth = 35;
    const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
    doc.addImage(logoImg, 'PNG', 14, currentY, logoWidth, logoHeight);
  } catch (error) {
    doc.setFontSize(20);
    doc.setTextColor(...colors.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('YDG', 14, currentY + 12);
  }
  
  // Title (center)
  doc.setFontSize(18);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'bold');
  doc.text('COMMISSION PAYOUT SUMMARY', pageWidth / 2, currentY + 22, { align: 'center' });
  
  currentY += 40;
  
  // Divider line
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.5);
  doc.line(14, currentY, pageWidth - 14, currentY);
  
  currentY += 10;
  
  // Period and date info
  doc.setFontSize(10);
  doc.setTextColor(...colors.muted);
  doc.text('PERIOD', 14, currentY);
  doc.setTextColor(...colors.dark);
  doc.text(`: ${period}`, 40, currentY);
  
  doc.setTextColor(...colors.muted);
  doc.text('GENERATED', pageWidth - 80, currentY);
  doc.setTextColor(...colors.dark);
  doc.text(`: ${formatShortDate(new Date())}`, pageWidth - 55, currentY);
  
  currentY += 15;
  
  // Calculate total
  const totalPayout = data.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  
  // Payout table
  const tableData = data.map((item, index) => [
    index + 1,
    item.user || 'N/A',
    item.group || 'N/A',
    formatCurrency(item.amount),
  ]);
  
  autoTable(doc, {
    startY: currentY,
    head: [['No.', 'Employee Name', 'Group', 'Payout Amount (MYR)']],
    body: tableData,
    foot: [['', '', 'TOTAL PAYOUT', formatCurrency(totalPayout)]],
    theme: 'grid',
    headStyles: {
      fillColor: colors.primary,
      textColor: colors.white,
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 4,
    },
    footStyles: {
      fillColor: colors.secondary,
      textColor: colors.white,
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 4,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 3,
      textColor: colors.dark,
    },
    alternateRowStyles: {
      fillColor: colors.lightBg,
    },
    columnStyles: {
      0: { cellWidth: 15, halign: 'center' },
      1: { cellWidth: 70 },
      2: { cellWidth: 50 },
      3: { cellWidth: 45, halign: 'right' },
    },
    margin: { left: 14, right: 14 },
  });
  
  // Add footer to all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...colors.muted);
    doc.text(`Page ${i} of ${totalPages}`, 14, pageHeight - 10, { align: 'left' });
    doc.text(`This is a computer-generated document. Generated on ${formatDateTime()}`, pageWidth - 14, pageHeight - 10, { align: 'right' });
  }
  
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  return pdfUrl;
};

// Commission Summary Report (for All Users or Group)
export const generateCommissionSummaryPDF = async (data, period, groupName = null, totalRetroactive = 0) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  let currentY = 15;
  
  // Logo (top left)
  try {
    const logoImg = new Image();
    logoImg.crossOrigin = 'Anonymous';
    
    await new Promise((resolve, reject) => {
      logoImg.onload = resolve;
      logoImg.onerror = reject;
      logoImg.src = '/images/YDG_LOGO.png';
    });
    
    const logoWidth = 35;
    const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
    doc.addImage(logoImg, 'PNG', 14, currentY, logoWidth, logoHeight);
  } catch (error) {
    doc.setFontSize(20);
    doc.setTextColor(...colors.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('YDG', 14, currentY + 12);
  }
  
  // Title
  const title = groupName 
    ? `COMMISSION SUMMARY - ${groupName.toUpperCase()}`
    : 'COMMISSION SUMMARY REPORT';
  
  doc.setFontSize(16);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'bold');
  doc.text(title, pageWidth / 2, currentY + 22, { align: 'center' });
  
  currentY += 40;
  
  // Divider line
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.5);
  doc.line(14, currentY, pageWidth - 14, currentY);
  
  currentY += 10;
  
  // Period info
  doc.setFontSize(10);
  doc.setTextColor(...colors.muted);
  doc.text('PERIOD', 14, currentY);
  doc.setTextColor(...colors.dark);
  doc.text(`: ${period}`, 40, currentY);
  
  doc.setTextColor(...colors.muted);
  doc.text('GENERATED', pageWidth - 80, currentY);
  doc.setTextColor(...colors.dark);
  doc.text(`: ${formatDateTime()}`, pageWidth - 55, currentY);
  
  currentY += 15;
  
  // Group data by group name
  const groupedData = {};
  let grandTotalPersonal = 0;
  let grandTotalTeam = 0;
  let grandTotalGroup = 0;
  let grandTotalRetro = 0;
  let grandTotal = 0;
  
  data.forEach(item => {
    const grpName = item.group || 'Unassigned';
    if (!groupedData[grpName]) {
      groupedData[grpName] = {
        users: [],
        totalPersonal: 0,
        totalTeam: 0,
        totalGroup: 0,
        totalRetro: 0,
        total: 0
      };
    }
    groupedData[grpName].users.push(item);
    groupedData[grpName].totalPersonal += item.personal || 0;
    groupedData[grpName].totalTeam += item.teamOverride || 0;
    groupedData[grpName].totalGroup += item.groupOverride || 0;
    groupedData[grpName].totalRetro += item.retroactive || 0;
    groupedData[grpName].total += item.total || 0;
    
    grandTotalPersonal += item.personal || 0;
    grandTotalTeam += item.teamOverride || 0;
    grandTotalGroup += item.groupOverride || 0;
    grandTotalRetro += item.retroactive || 0;
    grandTotal += item.total || 0;
  });
  
  // Summary section
  doc.setFontSize(11);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'bold');
  doc.text('OVERALL SUMMARY', 14, currentY);
  
  currentY += 8;
  
  // Summary table with grand total as last body row
  const summaryTableData = [
    ['Total Personal Commission', formatCurrency(grandTotalPersonal)],
    ['Total Team Override Commission', formatCurrency(grandTotalTeam)],
    ['Total Group Override Commission', formatCurrency(grandTotalGroup)],
  ];
  
  // Add retroactive row if there are retroactive commissions
  if (grandTotalRetro > 0 || totalRetroactive > 0) {
    summaryTableData.push(['Total Retroactive Commission (Prior Period)', formatCurrency(grandTotalRetro || totalRetroactive)]);
  }
  
  summaryTableData.push(['GRAND TOTAL ELIGIBLE COMMISSION', formatCurrency(grandTotal)]);
  
  const totalRowIndex = summaryTableData.length - 1;
  
  let summaryFinalY = currentY;
  autoTable(doc, {
    startY: currentY,
    head: [['Description', 'Amount (MYR)']],
    body: summaryTableData,
    theme: 'grid',
    headStyles: {
      fillColor: colors.primary,
      textColor: colors.white,
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 4,
      lineColor: colors.border,
      lineWidth: 0.1,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 4,
      textColor: colors.dark,
      lineColor: colors.border,
      lineWidth: 0.1,
    },
    columnStyles: {
      0: { cellWidth: 130, halign: 'left' },
      1: { cellWidth: 52, halign: 'right' },
    },
    margin: { left: 14, right: 14 },
    tableWidth: 182,
    didParseCell: (hookData) => {
      // Style the last row (total row) differently
      if (hookData.section === 'body' && hookData.row.index === totalRowIndex) {
        hookData.cell.styles.fillColor = colors.secondary;
        hookData.cell.styles.textColor = colors.white;
        hookData.cell.styles.fontStyle = 'bold';
        hookData.cell.styles.fontSize = 10;
      }
      // Style retroactive row with orange
      if (hookData.section === 'body' && hookData.row.raw && hookData.row.raw[0]?.includes('Retroactive')) {
        hookData.cell.styles.fillColor = [255, 248, 240];
        hookData.cell.styles.textColor = [230, 126, 34];
      }
    },
    didDrawPage: (hookData) => {
      summaryFinalY = hookData.cursor.y;
    },
  });
  
  currentY = summaryFinalY + 15;
  
  // Detail section - grouped by group
  doc.setFontSize(11);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'bold');
  doc.text('COMMISSION DETAILS BY USER', 14, currentY);
  
  currentY += 8;
  
  // Build table data with group headers
  const detailsData = [];
  
  // Role priority for sorting (Group Leader first, then Team Leader, then Agent)
  const rolePriority = {
    'Group Leader': 1,
    'Team Leader': 2,
    'Agent': 3
  };
  
  Object.keys(groupedData).sort().forEach(grpName => {
    const grp = groupedData[grpName];
    
    // Sort users within group by role hierarchy
    grp.users.sort((a, b) => {
      const priorityA = rolePriority[a.role] || 99;
      const priorityB = rolePriority[b.role] || 99;
      if (priorityA !== priorityB) return priorityA - priorityB;
      // Secondary sort by name
      return (a.name || '').localeCompare(b.name || '');
    });
    
    // Group header row - remove emoji to avoid encoding issues
    const groupHeaderText = `${grpName}`;
    detailsData.push([
      { content: groupHeaderText, colSpan: 7, styles: { fillColor: [230, 240, 240], fontStyle: 'bold', fontSize: 9, halign: 'left' } }
    ]);
    
    // User rows
    grp.users.forEach(user => {
      detailsData.push([
        user.name || 'N/A',
        user.code || 'N/A',
        user.role || 'N/A',
        formatCurrency(user.personal || 0),
        formatCurrency(user.teamOverride || 0),
        formatCurrency(user.groupOverride || 0),
        formatCurrency(user.total || 0),
      ]);
    });
    
    // Group subtotal
    detailsData.push([
      { content: `Subtotal `, colSpan: 3, styles: { fontStyle: 'bold', halign: 'left' } },
      { content: formatCurrency(grp.totalPersonal), styles: { fontStyle: 'bold' } },
      { content: formatCurrency(grp.totalTeam), styles: { fontStyle: 'bold' } },
      { content: formatCurrency(grp.totalGroup), styles: { fontStyle: 'bold' } },
      { content: formatCurrency(grp.total), styles: { fontStyle: 'bold', fillColor: colors.lightBg } },
    ]);
  });
  
  // Add grand total as last row of the details table
  detailsData.push([
    { content: 'GRAND TOTAL', colSpan: 3, styles: { fontStyle: 'bold', halign: 'right', fillColor: colors.secondary, textColor: colors.white } },
    { content: formatCurrency(grandTotalPersonal), styles: { fontStyle: 'bold', fillColor: colors.secondary, textColor: colors.white } },
    { content: formatCurrency(grandTotalTeam), styles: { fontStyle: 'bold', fillColor: colors.secondary, textColor: colors.white } },
    { content: formatCurrency(grandTotalGroup), styles: { fontStyle: 'bold', fillColor: colors.secondary, textColor: colors.white } },
    { content: formatCurrency(grandTotal), styles: { fontStyle: 'bold', fillColor: colors.secondary, textColor: colors.white } },
  ]);
  
  autoTable(doc, {
    startY: currentY,
    head: [['Name', 'Code', 'Role', 'Personal', 'Team O/R', 'Group O/R', 'Total']],
    body: detailsData,
    theme: 'grid',
    headStyles: {
      fillColor: colors.primary,
      textColor: colors.white,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: 3,
      lineColor: colors.border,
      lineWidth: 0.1,
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 2,
      textColor: colors.dark,
      lineColor: colors.border,
      lineWidth: 0.1,
    },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 22 },
      2: { cellWidth: 25 },
      3: { cellWidth: 25, halign: 'right' },
      4: { cellWidth: 25, halign: 'right' },
      5: { cellWidth: 25, halign: 'right' },
      6: { cellWidth: 25, halign: 'right' },
    },
    margin: { left: 14, right: 14 },
  });
  
  // Add footer to all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...colors.muted);
    doc.text(`Page ${i} of ${totalPages}`, 14, pageHeight - 10, { align: 'left' });
    doc.text(`This is a computer-generated document. Generated on ${formatDateTime()}`, pageWidth - 14, pageHeight - 10, { align: 'right' });
  }
  
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  return pdfUrl;
};

// Commission Comparison PDF (2 groups side-by-side in landscape)
export const generateCommissionComparisonPDF = async (data, period, groupNameA, groupNameB) => {
  const doc = new jsPDF({ orientation: 'landscape' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const gap = 10;

  const contentWidth = pageWidth - 2 * margin;
  const columnWidth = (contentWidth - gap) / 2;

  const leftA = margin;
  const rightA = pageWidth - (leftA + columnWidth);
  const leftB = margin + columnWidth + gap;
  const rightB = pageWidth - (leftB + columnWidth);

  let currentY = 15;

  // Logo (top left)
  try {
    const logoImg = new Image();
    logoImg.crossOrigin = 'Anonymous';
    await new Promise((resolve, reject) => {
      logoImg.onload = resolve;
      logoImg.onerror = reject;
      logoImg.src = '/images/YDG_LOGO.png';
    });
    const logoWidth = 35;
    const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
    doc.addImage(logoImg, 'PNG', margin, currentY, logoWidth, logoHeight);
  } catch (error) {
    doc.setFontSize(20);
    doc.setTextColor(...colors.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('YDG', margin, currentY + 12);
  }

  // Title
  doc.setFontSize(16);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'bold');
  doc.text('COMMISSION COMPARISON REPORT', pageWidth / 2, currentY + 22, { align: 'center' });

  currentY += 40;

  // Divider
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY, pageWidth - margin, currentY);

  currentY += 10;

  // Period / generated
  doc.setFontSize(10);
  doc.setTextColor(...colors.muted);
  doc.text('PERIOD', margin, currentY);
  doc.setTextColor(...colors.dark);
  doc.text(`: ${period}`, 40, currentY);

  doc.setTextColor(...colors.muted);
  doc.text('GENERATED', pageWidth - 90, currentY);
  doc.setTextColor(...colors.dark);
  doc.text(`: ${formatShortDate(new Date())}`, pageWidth - 35, currentY);

  currentY += 15;

  const rolePriority = {
    'Group Leader': 1,
    'Team Leader': 2,
    'Agent': 3,
  };

  const addFooters = () => {
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(...colors.muted);
      doc.text(`Page ${i} of ${totalPages}`, margin, pageHeight - 10, { align: 'left' });
      doc.text(
        `This is a computer-generated document. Generated on ${formatDateTime()}`,
        pageWidth - margin,
        pageHeight - 10,
        { align: 'right' }
      );
    }
  };

  const renderGrandTotalBox = (groupName, groupItems, startY, marginLeft, marginRight) => {
    const grandTotalPersonal = groupItems.reduce((sum, it) => sum + (it.personal || 0), 0);
    const grandTotalTeam = groupItems.reduce((sum, it) => sum + (it.teamOverride || 0), 0);
    const grandTotalGroup = groupItems.reduce((sum, it) => sum + (it.groupOverride || 0), 0);
    const grandTotalRetro = groupItems.reduce((sum, it) => sum + (it.retroactive || 0), 0);
    const grandTotal = groupItems.reduce((sum, it) => sum + (it.total || 0), 0);

    const summaryTableData = [
      ['Total Personal Commission', formatCurrency(grandTotalPersonal)],
      ['Total Team Override Commission', formatCurrency(grandTotalTeam)],
      ['Total Group Override Commission', formatCurrency(grandTotalGroup)],
    ];

    if (grandTotalRetro > 0) {
      summaryTableData.push([
        'Total Retroactive Commission (Prior Period)',
        formatCurrency(grandTotalRetro),
      ]);
    }

    summaryTableData.push(['GRAND TOTAL ELIGIBLE COMMISSION', formatCurrency(grandTotal)]);
    const totalRowIndex = summaryTableData.length - 1;

    // Group label above the summary table
    doc.setFontSize(9);
    doc.setTextColor(...colors.dark);
    doc.setFont('helvetica', 'bold');
    doc.text(`GROUP: ${groupName.toUpperCase()}`, marginLeft, startY - 4);

    autoTable(doc, {
      startY,
      head: [['Description', 'Amount (MYR)']],
      body: summaryTableData,
      theme: 'grid',
      tableWidth: columnWidth,
      headStyles: {
        fillColor: colors.primary,
        textColor: colors.white,
        fontStyle: 'bold',
        fontSize: 8,
        cellPadding: 3,
        lineColor: colors.border,
        lineWidth: 0.1,
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3,
        textColor: colors.dark,
        lineColor: colors.border,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: columnWidth * 0.72, halign: 'left' },
        1: { cellWidth: columnWidth * 0.28, halign: 'right' },
      },
      margin: { left: marginLeft, right: marginRight },
      didParseCell: (hookData) => {
        // Style the grand total row
        if (hookData.section === 'body' && hookData.row.index === totalRowIndex) {
          hookData.cell.styles.fillColor = colors.secondary;
          hookData.cell.styles.textColor = colors.white;
          hookData.cell.styles.fontStyle = 'bold';
        }
      },
    });

    return doc.lastAutoTable.finalY;
  };

  const renderDetailsTable = (groupName, groupItems, startY) => {
    let y = startY;

    doc.setFontSize(11);
    doc.setTextColor(...colors.dark);
    doc.setFont('helvetica', 'bold');
    doc.text(`COMMISSION DETAILS BY USER - ${groupName.toUpperCase()}`, margin, y);
    y += 8;

    const grandTotalPersonal = groupItems.reduce((sum, it) => sum + (it.personal || 0), 0);
    const grandTotalTeam = groupItems.reduce((sum, it) => sum + (it.teamOverride || 0), 0);
    const grandTotalGroup = groupItems.reduce((sum, it) => sum + (it.groupOverride || 0), 0);
    const grandTotal = groupItems.reduce((sum, it) => sum + (it.total || 0), 0);

    const sortedUsers = groupItems.slice().sort((a, b) => {
      const pa = rolePriority[a.role] || 99;
      const pb = rolePriority[b.role] || 99;
      if (pa !== pb) return pa - pb;
      return (a.name || '').localeCompare(b.name || '');
    });

    const detailsData = [];
    detailsData.push([
      {
        content: groupName,
        colSpan: 7,
        styles: { fillColor: [230, 240, 240], fontStyle: 'bold', fontSize: 9, halign: 'left' },
      },
    ]);

    sortedUsers.forEach((user) => {
      detailsData.push([
        user.name || 'N/A',
        user.code || 'N/A',
        user.role || 'N/A',
        formatCurrency(user.personal || 0),
        formatCurrency(user.teamOverride || 0),
        formatCurrency(user.groupOverride || 0),
        formatCurrency(user.total || 0),
      ]);
    });

    detailsData.push([
      { content: 'Subtotal ', colSpan: 3, styles: { fontStyle: 'bold', halign: 'left' } },
      { content: formatCurrency(grandTotalPersonal), styles: { fontStyle: 'bold' } },
      { content: formatCurrency(grandTotalTeam), styles: { fontStyle: 'bold' } },
      { content: formatCurrency(grandTotalGroup), styles: { fontStyle: 'bold' } },
      { content: formatCurrency(grandTotal), styles: { fontStyle: 'bold', fillColor: colors.lightBg } },
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Name', 'Code', 'Role', 'Personal', 'Team O/R', 'Group O/R', 'Total']],
      body: detailsData,
      theme: 'grid',
      tableWidth: contentWidth,
      headStyles: {
        fillColor: colors.primary,
        textColor: colors.white,
        fontStyle: 'bold',
        fontSize: 8,
        cellPadding: 3,
        lineColor: colors.border,
        lineWidth: 0.1,
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 2,
        textColor: colors.dark,
        lineColor: colors.border,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: contentWidth * (35 / 144) },
        1: { cellWidth: contentWidth * (22 / 144) },
        2: { cellWidth: contentWidth * (25 / 144) },
        3: { cellWidth: contentWidth * (25 / 144), halign: 'right' },
        4: { cellWidth: contentWidth * (25 / 144), halign: 'right' },
        5: { cellWidth: contentWidth * (25 / 144), halign: 'right' },
        6: { cellWidth: contentWidth * (25 / 144), halign: 'right' },
      },
      margin: { left: margin, right: margin },
    });

    return doc.lastAutoTable.finalY;
  };

  const dataA = (data || []).filter((it) => (it.group || '') === groupNameA);
  const dataB = (data || []).filter((it) => (it.group || '') === groupNameB);

  // Render grand total side-by-side first.
  doc.setFontSize(11);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'bold');
  doc.text('GRAND TOTAL ELIGIBLE COMMISSION (COMPARISON)', margin, currentY);
  currentY += 6;

  const yGrandStart = currentY;
  const endA = renderGrandTotalBox(groupNameA, dataA, yGrandStart, leftA, rightA);
  const endB = renderGrandTotalBox(groupNameB, dataB, yGrandStart, leftB, rightB);
  currentY = Math.max(endA, endB) + 12;

  // Then stack commission details vertically (up / bottom).
  const endDetailsA = renderDetailsTable(groupNameA, dataA, currentY);
  renderDetailsTable(groupNameB, dataB, endDetailsA + 12);

  addFooters();

  const pdfBlob = doc.output('blob');
  return URL.createObjectURL(pdfBlob);
};

// Payment History Statement PDF (investor portal - paid dividends by enrollment)
export const generatePaymentHistoryStatementPDF = async (profile) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  let currentY = 15;

  // Logo (top left)
  try {
    const logoImg = new Image();
    logoImg.crossOrigin = 'Anonymous';
    await new Promise((resolve, reject) => {
      logoImg.onload = resolve;
      logoImg.onerror = reject;
      logoImg.src = '/images/YDG_LOGO.png';
    });
    const logoWidth = 35;
    const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
    doc.addImage(logoImg, 'PNG', margin, currentY, logoWidth, logoHeight);
  } catch (error) {
    doc.setFontSize(20);
    doc.setTextColor(...colors.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('YDG', margin, currentY + 12);
  }

  currentY += 28;

  // Title
  doc.setFontSize(18);
  doc.setTextColor(...colors.dark);
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Received Statement', pageWidth / 2, currentY, { align: 'center' });

  currentY += 8;

  // Two columns: Left = name, account number, address; Right = email, identification number, statement date
  const lineHeight = 6;
  const rightX = pageWidth - margin;
  doc.setFontSize(10);
  doc.setTextColor(...colors.muted);
  doc.setFont('helvetica', 'normal');

  const icDisplay = profile.icNumber
    ? (profile.icNumber.replace(/\D/g, '').length === 12
        ? `${profile.icNumber.replace(/\D/g, '').slice(0, 6)}-${profile.icNumber.replace(/\D/g, '').slice(6, 8)}-${profile.icNumber.replace(/\D/g, '').slice(8, 12)}`
        : profile.icNumber)
    : 'N/A';

  // Build address lines: line1, line2, line3 = postcode + city, line4 = state, line5 = country (only if not Malaysia)
  const postcodeCity = [profile.postcode, profile.city].filter(Boolean).join(' ');
  const showCountry = profile.country && profile.country.trim().toLowerCase() !== 'malaysia';
  const addressLines = [
    profile.addressLine1,
    profile.addressLine2,
    postcodeCity,
    profile.state,
    ...(showCountry ? [profile.country] : []),
  ].filter(Boolean);
  const addressDisplayLines = addressLines.length > 0 ? addressLines : ['N/A'];

  let leftY = currentY;
  let rightY = currentY;

  // Left column: Investor name
  doc.text(`Investor: ${profile.name || 'N/A'}`, margin, leftY);
  leftY += lineHeight;
  // Account number
  doc.text(`Account number: ${profile.accountNumber || 'N/A'}`, margin, leftY);
  leftY += lineHeight;
  // Address (content aligned with "Address:" label, no indent)
  doc.text('Address:', margin, leftY);
  leftY += lineHeight;
  addressDisplayLines.forEach((line) => {
    doc.text(line, margin, leftY);
    leftY += lineHeight;
  });

  // Right column: Email, Identification number, Statement date
  doc.text(`Email: ${profile.email || 'N/A'}`, rightX, rightY, { align: 'right' });
  rightY += lineHeight;
  doc.text(`Identification number: ${icDisplay}`, rightX, rightY, { align: 'right' });
  rightY += lineHeight;
  doc.text(`Statement date: ${formatShortDate(new Date())}`, rightX, rightY, { align: 'right' });

  currentY = Math.max(leftY, rightY) + 6;

  // Divider
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY, pageWidth - margin, currentY);
  currentY += 12;

  const contentWidth = pageWidth - 2 * margin;

  const enrollments = profile.enrollments || [];
  const enrollmentsWithPaid = enrollments.filter((enr) => {
    const paid = (enr.dividendPayments || []).filter((d) => d.status === 'Paid' && d.paidDate);
    return paid.length > 0;
  });

  if (enrollmentsWithPaid.length === 0) {
    doc.setFontSize(11);
    doc.setTextColor(...colors.muted);
    doc.text('No payments received yet for any enrollment.', margin, currentY);
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    return pdfUrl;
  }

  for (const enr of enrollmentsWithPaid) {
    const paidDividends = (enr.dividendPayments || []).filter((d) => d.status === 'Paid' && d.paidDate);
    const productName = enr.productDetails?.product?.name || 'Enrollment';
    const enrollmentAmount = formatCurrency(enr.amount);

    // Check for new page (enrollment header + table needs space)
    if (currentY > pageHeight - 60) {
      doc.addPage();
      currentY = 20;
    }

    doc.setFontSize(12);
    doc.setTextColor(...colors.dark);
    doc.setFont('helvetica', 'bold');
    doc.text(`${productName} — ${enrollmentAmount}`, margin, currentY);
    currentY += 8;

    const tableData = paidDividends.map((d) => [
      `Year ${d.year}`,
      formatMonthYear(d.dueDate),
      formatCurrency(d.amount),
      formatDate(d.paidDate),
    ]);

    // Column width proportions (sum 142) scaled to content width so table aligns with divider
    const colRatios = [22, 35, 45, 40];
    const colWidths = colRatios.map((r) => (r / 142) * contentWidth);
    autoTable(doc, {
      startY: currentY,
      head: [['Year', 'Due date', 'Amount', 'Paid date']],
      body: tableData,
      theme: 'plain',
      tableWidth: contentWidth,
      headStyles: {
        fillColor: colors.lightBg,
        textColor: colors.secondary,
        fontStyle: 'bold',
        fontSize: 9,
      },
      bodyStyles: { fontSize: 9, textColor: colors.dark },
      columnStyles: {
        0: { cellWidth: colWidths[0] },
        1: { cellWidth: colWidths[1] },
        2: { cellWidth: colWidths[2], halign: 'center' },
        3: { cellWidth: colWidths[3] },
      },
      margin: { left: margin, right: margin },
    });

    currentY = doc.lastAutoTable.finalY + 14;
  }

  // Footer on all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...colors.muted);
    doc.text(`Page ${i} of ${totalPages}`, margin, pageHeight - 10, { align: 'left' });
    doc.text(`Generated on ${formatDateTime()}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  return pdfUrl;
}

// Use <a download> + click so mobile browsers don't block (window.open is often blocked on mobile)
export function triggerPdfDownload(blobUrl, filename) {
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename || 'document.pdf';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 200);
}

export default {
  generateCommissionPDF,
  generateSalesReportPDF,
  generatePayoutReportPDF,
  generateCommissionSummaryPDF,
  generateSalesComparisonPDF,
  generateCommissionComparisonPDF,
  generatePaymentHistoryStatementPDF,
  triggerPdfDownload,
};
